import { useCallback, useEffect, useRef } from "react";
import type React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useExplorerStore } from "../store/explorerStore";
import { filesQueryOptions } from "./useFileExplorer";

/** How long the pointer must rest on a folder before its files load, so sweeping across the grid fetches nothing. */
const HOVER_DELAY_MS = 120;

/**
 * Loads a folder's first page of files when the pointer rests on it (or a finger touches it), so
 * the folder usually opens from cache. Fetches nothing if that page is already cached and fresh.
 */
export function usePrefetchFolder(folderId: string) {
  const qc = useQueryClient();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  useEffect(() => cancel, [cancel]);

  const prefetch = useCallback(() => {
    const { sort, order } = useExplorerStore.getState();
    void qc.prefetchInfiniteQuery(filesQueryOptions(folderId, sort, order));
  }, [qc, folderId]);

  const onPointerEnter = useCallback(
    (e: React.PointerEvent) => {
      cancel();
      if (e.pointerType === "touch") prefetch();
      else timer.current = setTimeout(prefetch, HOVER_DELAY_MS);
    },
    [cancel, prefetch]
  );

  return { onPointerEnter, onPointerLeave: cancel };
}
